import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

// extend chỉ THÊM API (isoWeek/quarter), không đổi hành vi có sẵn của dayjs.
// Cố ý KHÔNG dùng updateLocale để đổi weekStart: cái đó sẽ đổi ngày bắt đầu tuần của
// MỌI RangePicker trong app. Muốn tuần bắt đầu Thứ 2 thì dùng thẳng startOf('isoWeek').
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

export type OrderDatePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_6_days'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'year_to_date';

export interface OrderDatePreset {
  key: OrderDatePresetKey;
  label: string;
  group: string;
  /** Nhận `now` để test đóng băng được đồng hồ ở các mốc hiểm (31/12, cuối tháng…). */
  resolve: (now: Dayjs) => [Dayjs, Dayjs];
}

export const ORDER_DATE_PRESETS: readonly OrderDatePreset[] = [
  {
    key: 'today',
    label: 'Hôm nay',
    group: 'Ngày',
    resolve: (n) => [n.startOf('day'), n.endOf('day')],
  },
  {
    key: 'yesterday',
    label: 'Hôm qua',
    group: 'Ngày',
    resolve: (n) => [n.subtract(1, 'day').startOf('day'), n.subtract(1, 'day').endOf('day')],
  },
  {
    // TCR-1092: mặc định của trang Đơn hàng — "hôm nay lùi 5 ngày" theo yêu cầu tester,
    // khớp `DEFAULT_DATE_RANGE_DAYS = 5` mà trang Đơn ECom đang dùng.
    key: 'last_6_days',
    label: '6 ngày qua',
    group: 'Ngày',
    resolve: (n) => [n.subtract(5, 'day').startOf('day'), n.endOf('day')],
  },
  {
    // Tính CẢ hôm nay: "7 ngày qua" = hôm nay + 6 ngày trước đó.
    key: 'last_7_days',
    label: '7 ngày qua',
    group: 'Ngày',
    resolve: (n) => [n.subtract(6, 'day').startOf('day'), n.endOf('day')],
  },
  {
    key: 'last_30_days',
    label: '30 ngày qua',
    group: 'Ngày',
    resolve: (n) => [n.subtract(29, 'day').startOf('day'), n.endOf('day')],
  },
  {
    key: 'this_week',
    label: 'Tuần này',
    group: 'Tuần',
    resolve: (n) => [n.startOf('isoWeek'), n.endOf('isoWeek')],
  },
  {
    key: 'last_week',
    label: 'Tuần trước',
    group: 'Tuần',
    resolve: (n) => [
      n.subtract(1, 'week').startOf('isoWeek'),
      n.subtract(1, 'week').endOf('isoWeek'),
    ],
  },
  {
    key: 'this_month',
    label: 'Tháng này',
    group: 'Tháng',
    resolve: (n) => [n.startOf('month'), n.endOf('month')],
  },
  {
    key: 'last_month',
    label: 'Tháng trước',
    group: 'Tháng',
    resolve: (n) => [
      n.subtract(1, 'month').startOf('month'),
      n.subtract(1, 'month').endOf('month'),
    ],
  },
  {
    key: 'this_quarter',
    label: 'Quý này',
    group: 'Quý / Năm',
    resolve: (n) => [n.startOf('quarter'), n.endOf('quarter')],
  },
  {
    key: 'this_year',
    label: 'Năm nay',
    group: 'Quý / Năm',
    resolve: (n) => [n.startOf('year'), n.endOf('year')],
  },
  {
    // Khác "Năm nay" ở đầu cuối: YTD dừng ở hôm nay, nên loại đơn có ngày ở tương lai.
    key: 'year_to_date',
    label: 'Từ đầu năm',
    group: 'Quý / Năm',
    resolve: (n) => [n.startOf('year'), n.endOf('day')],
  },
] as const;

export const ORDER_DATE_PRESET_GROUPS: readonly string[] = ['Ngày', 'Tuần', 'Tháng', 'Quý / Năm'];

const PRESET_BY_KEY = new Map(ORDER_DATE_PRESETS.map((p) => [p.key as string, p]));

export const ORDER_DATE_PRESET_LABEL: Record<string, string> = Object.fromEntries(
  ORDER_DATE_PRESETS.map((p) => [p.key, p.label]),
);

export const ORDER_DATE_PRESET_THIS_MONTH: OrderDatePresetKey = 'this_month';

/**
 * Preset mặc định của trang Đơn hàng (TCR-1092 vòng 2: đổi từ `this_month`).
 *
 * Là PRESET KEY chứ không phải cặp ngày tuyệt đối: snapshot filter nằm ở localStorage,
 * ghi ngày cứng sẽ khiến phiên đăng nhập kéo sang hôm sau vẫn lọc theo ngày cũ.
 */
export const ORDER_DATE_PRESET_DEFAULT: OrderDatePresetKey = 'last_6_days';

/**
 * Trả về khoảng ngày của preset, hoặc `null` nếu key không hợp lệ.
 *
 * Key lạ (link cũ, gõ tay) trả null để phía trên KHÔNG lọc ngày, thay vì im lặng áp
 * mặc định — áp mặc định sẽ khiến người nhận link tưởng link vẫn đúng.
 */
export function resolveOrderDatePreset(
  key: string | undefined,
  now: Dayjs = dayjs(),
): [Dayjs, Dayjs] | null {
  if (!key) return null;
  const preset = PRESET_BY_KEY.get(key);
  return preset ? preset.resolve(now) : null;
}

export function isOrderDatePresetKey(key: string | undefined): boolean {
  return !!key && PRESET_BY_KEY.has(key);
}
